import { useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import api from '../api';

// Campo de vídeo do YouTube com 3 caminhos:
//  - colar o ID/link na mão (sempre disponível, fallback);
//  - "Buscar do canal" → lista os vídeos via API e liga num clique (consulta/sync);
//  - "Enviar vídeo" → sobe o arquivo pro YouTube (unlisted) e preenche o ID (upload).
// Os 2 botões só aparecem se a integração estiver configurada no servidor (.env).
export default function YoutubePicker({ value, onChange }) {
  const [configurado, setConfigurado] = useState(false);
  const [lista, setLista] = useState(null);        // null = fechado; [] = aberto/carregando
  const [carregando, setCarregando] = useState(false);
  const [busca, setBusca] = useState('');
  const [enviando, setEnviando] = useState(0);     // 0 = parado; 1..100 = % do upload
  const fileRef = useRef(null);

  useEffect(() => {
    api.get('/youtube/status').then(({ data }) => setConfigurado(!!data.configurado)).catch(() => {});
  }, []);

  async function abrirLista() {
    setLista([]); setCarregando(true); setBusca('');
    try {
      const { data } = await api.get('/youtube/videos');
      setLista(data.videos || []);
    } catch (e) {
      setLista(null);
      Swal.fire('Não deu pra listar', e.response?.data?.error || 'Falha ao consultar o YouTube.', 'error');
    } finally {
      setCarregando(false);
    }
  }

  async function enviarArquivo(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const fd = new FormData();
    fd.append('video', file);
    fd.append('titulo', file.name.replace(/\.[^.]+$/, ''));
    setEnviando(1);
    try {
      const { data } = await api.post('/youtube/upload', fd, {
        onUploadProgress: (p) => setEnviando(Math.max(1, Math.round((p.loaded / (p.total || 1)) * 100))),
      });
      onChange(data.youtube_id);
      Swal.fire({ icon: 'success', title: 'Vídeo enviado!', text: 'Subiu como "não listado" e já ligamos aqui.', timer: 2000, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Falha no envio', err.response?.data?.error || 'Não foi possível enviar o vídeo.', 'error');
    } finally {
      setEnviando(0);
    }
  }

  const filtrados = (lista || []).filter(v => !busca || (v.titulo || '').toLowerCase().includes(busca.toLowerCase()));

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          className="doc-input" style={{ flex: '1 1 220px', margin: 0 }}
          value={value || ''} onChange={e => onChange(extrairYoutube(e.target.value))}
          placeholder="ID ou link do YouTube (ex.: dQw4w9WgXcQ)"
        />
        {configurado && (
          <>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={abrirLista} disabled={enviando > 0}>
              <i className="bi bi-search"></i> Buscar do canal
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => fileRef.current?.click()} disabled={enviando > 0}>
              <i className="bi bi-cloud-arrow-up"></i> Enviar vídeo
            </button>
            <input ref={fileRef} type="file" accept="video/*" style={{ display: 'none' }} onChange={enviarArquivo} />
          </>
        )}
      </div>

      {enviando > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 13, color: 'var(--verde)', marginBottom: 4 }}>Enviando pro YouTube… {enviando}%</div>
          <div style={{ height: 6, background: '#e9ecef', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${enviando}%`, background: 'var(--verde)', transition: 'width .2s' }} />
          </div>
        </div>
      )}

      {value && enviando === 0 && (
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src={`https://i.ytimg.com/vi/${value}/mqdefault.jpg`} alt="" width={120}
               style={{ borderRadius: 6, border: '1px solid #e9ecef' }} onError={e => { e.target.style.display = 'none'; }} />
          <a href={`https://youtu.be/${value}`} target="_blank" rel="noreferrer" style={{ fontSize: 13 }}>
            Ver no YouTube <i className="bi bi-box-arrow-up-right"></i>
          </a>
        </div>
      )}

      {lista !== null && (
        <div onClick={() => setLista(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1050, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '6vh 12px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, width: 'min(640px,100%)', maxHeight: '82vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 10 }}>
              <strong style={{ color: 'var(--verde)' }}>Vídeos do canal</strong>
              <input className="doc-input" style={{ margin: 0, flex: 1 }} placeholder="Filtrar por título…" value={busca} onChange={e => setBusca(e.target.value)} autoFocus />
              <button type="button" className="btn-close" onClick={() => setLista(null)} aria-label="Fechar" />
            </div>
            <div style={{ overflowY: 'auto', padding: 8 }}>
              {carregando && <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>Carregando do YouTube…</div>}
              {!carregando && filtrados.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>Nenhum vídeo encontrado.</div>}
              {!carregando && filtrados.map(v => (
                <button type="button" key={v.id} onClick={() => { onChange(v.id); setLista(null); }}
                        style={{ display: 'flex', gap: 10, alignItems: 'center', width: '100%', textAlign: 'left', border: 'none', background: v.id === value ? '#eef7ef' : 'transparent', borderRadius: 8, padding: 8, cursor: 'pointer' }}>
                  <img src={`https://i.ytimg.com/vi/${v.id}/mqdefault.jpg`} alt="" width={96} height={54}
                       style={{ borderRadius: 6, objectFit: 'cover', flexShrink: 0, background: '#eee' }} onError={e => { e.target.style.visibility = 'hidden'; }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.titulo}</div>
                    <div style={{ fontSize: 12, color: '#888' }}>
                      {v.privacidade === 'unlisted' ? 'não listado' : v.privacidade}
                      {v.id === value && ' · selecionado'}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Aceita ID puro ou link do YouTube e extrai o ID.
function extrairYoutube(v) {
  const s = (v || '').trim();
  const m = s.match(/(?:youtu\.be\/|v=|embed\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : s;
}
